import React, { useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withLocalize, Translate } from 'react-localize-redux'
import { drills } from 'redux/modules'
import { createSubUrl } from 'utils/urlConstructor'
import filterObject from 'utils/filterObject'
import getTargetTranslationFields from 'utils/getTargetTranslationFields'
import DrillCreateEditForm from 'containers/ActivityCreateContainer/components/DrillCreateEditForm'
import PropTypes from 'prop-types'
import { ACTIVITIES_ROUTE } from 'constants/routes'

const FIELDS_WITH_TRANSLATION = [
  'title',
  'description',
  'drill_flow',
  'organization',
  'coaching_points',
  'variations',
]

const DrillEditContainer = ({
  history,
  match: {
    params: { teamUuid, drillUuid },
  },
  match,
  location: { fromUrl, fromPathname },
  drill: { translations, drillsCategories, ...drill },
  updateDrill,
  deleteDrill,
  activeLanguage: { code: locale },
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(locale || 'en')

  const linkBack =
    fromUrl ||
    createSubUrl(match, ACTIVITIES_ROUTE.populateURLParams({ drillUuid }))

  const onSubmit = ({
    duration,
    min_players: minPlayers,
    max_players: maxPlayers,
    images,
    title,
    description,
    organization,
    drill_flow: drillFlow,
    coaching_points: coachingPoints,
    variations,
    ...values
  }) => {
    const data = {
      ...values,
      duration: +duration,
      min_players: +minPlayers,
      max_players: +maxPlayers,
      team_id: teamUuid,
      title: title !== undefined ? title : '',
      description: description !== undefined ? description : '',
      organization: organization !== undefined ? organization : '',
      drill_flow: drillFlow !== undefined ? drillFlow : '',
      coaching_points: coachingPoints !== undefined ? coachingPoints : '',
      variations: variations !== undefined ? variations : '',
    }
    // fix when switch to v2
    data.categories = data.categories
      .map((uuid) => {
        const matchedCategory = drillsCategories.find(
          (cat) => cat.uuid === uuid
        )

        return matchedCategory ? matchedCategory.id : null
      })
      .filter((id) => id !== null)

    if (!images) {
      data[`images[0]`] = null
    } else if (typeof images === 'string') {
      data[`images[0]`] = drill.images[drill.images.length - 1].id
    } else {
      data[`images[0]`] = images
    }

    if (title !== '' && title !== undefined) {
      updateDrill(drillUuid, data)
    }
  }

  const getAgeClassUuids = (ageclasses) =>
    ageclasses.map((ageclass) => ageclass.uuid)

  const initialValues = {
    global: 0,
    ...filterObject(drill, Boolean),
    ...getTargetTranslationFields(
      translations,
      selectedLanguage,
      FIELDS_WITH_TRANSLATION
    ),
    locale: selectedLanguage,
    age_class: getAgeClassUuids(drill.age_classes),
    images:
      drill && drill?.images.length > 0
        ? drill.images[drill.images.length - 1].full_url
        : null,

    // fix when switch to v2, needed since we use v1
    categories:
      drill.categories
        ?.map((categoryId) => {
          const matchedCategory = drillsCategories.find(
            (cat) => cat.id === categoryId
          )

          return matchedCategory ? matchedCategory.uuid : null
        })
        .filter((uuid) => uuid !== null) || [],
  }

  const handleDelete = () =>
    deleteDrill(drillUuid).then(() => history.push(linkBack))

  return (
    <Translate>
      {({ translate }) => (
        <DrillCreateEditForm
          form="drillEdit"
          initialValues={initialValues}
          caption={translate('drills.edit-drill')}
          fromPath={{
            value: linkBack,
            title: fromPathname || translate('drills.own-drills'),
          }}
          onSubmit={onSubmit}
          drillsCategories={drillsCategories}
          isEditForm
          showDeleteButton
          handleDelete={handleDelete}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
        />
      )}
    </Translate>
  )
}

DrillEditContainer.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  drill: PropTypes.shape().isRequired,
  updateDrill: PropTypes.func.isRequired,
  deleteDrill: PropTypes.func.isRequired,
  activeLanguage: PropTypes.shape({
    code: PropTypes.string.isRequired,
  }).isRequired,
}

export default compose(
  connect(
    ({ drills: { current: drill } }) => ({
      drill,
    }),
    {
      updateDrill: drills.updateDrill,
      deleteDrill: drills.deleteDrill,
    }
  ),
  withLocalize
)(DrillEditContainer)
